import React from 'react';

import type { PlaybookAiGenerationResult } from '@aline/common';
import { PlaybookAiGenerationResultSchema } from '@aline/common';
import { link, routes } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { useMatch } from 'react-router-dom';
import { PlaybookChip } from 'src/editor/toolbars/right-panel/ai/playbooks';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  clickableRoot: {
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    cursor: 'pointer',
    transition: theme.transitions.create('border-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  titleText: {
    minWidth: 0,
  },
}));

export const CodePlaybookGenerationElement = React.memo(function CodePlaybookGenerationElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(
    codeBlockInfo.code,
    PlaybookAiGenerationResultSchema,
    'CodePlaybookGenerationElement',
    { captureErrors: !codeBlockInfo.isNodeStreaming }
  );
  if (!parsed.ok) {
    if (codeBlockInfo.isNodeStreaming) {
      return (
        <div className={classes.root}>
          <PlaybookGenerationLoadingBody />
        </div>
      );
    }

    return (
      <StaticCard
        detail="Could not display this playbook generation."
        title="Playbook generation unavailable"
      />
    );
  }

  return <PlaybookGenerationCard result={parsed.data} />;
});

function PlaybookGenerationLoadingBody() {
  const { classes } = useStyles();

  return (
    <div className={classes.body}>
      <div className={classes.titleRow}>
        <Skeleton className={classes.titleText} height={24} variant="text" width={140} />
        <Skeleton height={18} variant="rectangular" width={60} />
      </div>
      <Skeleton height={16} variant="text" width={120} />
    </div>
  );
}

function StaticCard({ detail, title }: { detail: string; title: string }) {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      <Typography className={classes.meta}>{detail}</Typography>
    </div>
  );
}

function PlaybookGenerationCard({ result }: { result: PlaybookAiGenerationResult }) {
  const { classes } = useStyles();
  const isOnChatRoute = Boolean(useMatch({ end: false, path: routes.ChatIndex }));
  /**
   * Live updates arrive through the org-wide PlaybookGenerationJobUpdated
   * subscription that ChatPanel registers once per thread carrying a
   * generation card, so this needs no polling and no per-card subscription.
   */
  const { data, loading } = Apollo.useGetPlaybookGenerationJobQuery({
    variables: { jobId: result.generationJobId },
  });

  if (loading && !data) {
    return (
      <div className={classes.root}>
        <PlaybookGenerationLoadingBody />
      </div>
    );
  }

  const generationJob = data?.playbookGenerationJob;
  /**
   * Cancelling a generation deletes its job, so a job that has gone missing is
   * the ordinary way a cancelled generation reads. Saying so beats a card that
   * shows a generation running forever.
   */
  if (!generationJob) {
    return (
      <StaticCard
        detail="This generation was cancelled or is no longer available."
        title="Playbook generation stopped"
      />
    );
  }

  if (generationJob.failedAt)
    return (
      <FailedPlaybookGenerationCard generationJob={generationJob} isOnChatRoute={isOnChatRoute} />
    );

  const { playbook } = generationJob;
  /**
   * The playbook exists from the moment the run starts, so it says nothing
   * about whether the run is done. `finishedAt` is what does.
   */
  if (!generationJob.finishedAt)
    return <GeneratingPlaybookCard generationJob={generationJob} isOnChatRoute={isOnChatRoute} />;

  /**
   * A finished job with nothing behind it means the playbook was deleted after
   * the run completed, so there is nothing left to open.
   */
  if (!playbook) {
    return (
      <StaticCard
        detail="This playbook is no longer available."
        title="Playbook generation finished"
      />
    );
  }

  return <GeneratedPlaybookCard isOnChatRoute={isOnChatRoute} playbook={playbook} />;
}

/** The playbook a card can open: enough to route to it and name the tab. */
interface OpenablePlaybook {
  id: string;
  title: string;
}

/**
 * From the full-page chat, take the current tab so the playbook comes into
 * focus. From anywhere else, open a tab of its own rather than hijacking the
 * one the user is working in.
 */
function openGeneratedPlaybook({
  isOnChatRoute,
  playbook,
}: {
  isOnChatRoute: boolean;
  playbook: OpenablePlaybook;
}) {
  openLink(link.PlaybooksDetail({ playbookId: playbook.id }), {
    newTab: !isOnChatRoute,
    title: playbook.title,
  });
}

/**
 * A run reports itself category by category, so the card can say how far along
 * it is rather than only that it is going.
 *
 * The playbook exists from the moment the run starts and its page fills in as
 * each category lands, so the card opens it while the run is still going
 * rather than making the user wait for the finished one.
 */
function GeneratingPlaybookCard({
  generationJob,
  isOnChatRoute,
}: {
  generationJob: {
    openCategory?: { name: string } | null;
    playbook?: OpenablePlaybook | null;
  };
  isOnChatRoute: boolean;
}) {
  const { classes } = useStyles();
  const { openCategory, playbook } = generationJob;

  function openPlaybook() {
    if (!playbook) return;
    openGeneratedPlaybook({ isOnChatRoute, playbook });
  }

  return (
    <div
      className={clsx(classes.root, Boolean(playbook) && classes.clickableRoot)}
      onClick={openPlaybook}
    >
      <div className={classes.body}>
        <div className={classes.titleRow}>
          <Typography noWrap className={classes.titleText} sx={{ fontWeight: 600 }}>
            {playbook?.title || 'Generating playbook...'}
          </Typography>
          <PlaybookChip label="Generating" tone="running" />
        </div>
        <Typography className={classes.meta}>{getGenerationStatusLine(openCategory)}</Typography>
      </div>
    </div>
  );
}

function getGenerationStatusLine(openCategory: { name: string } | null | undefined) {
  if (openCategory?.name) return `Writing "${openCategory.name}"…`;
  return 'Reading the document and writing the playbook.';
}

function GeneratedPlaybookCard({
  isOnChatRoute,
  playbook,
}: {
  isOnChatRoute: boolean;
  playbook: OpenablePlaybook & { categories: unknown[]; ruleCount: number };
}) {
  const { classes } = useStyles();

  function openPlaybook() {
    openGeneratedPlaybook({ isOnChatRoute, playbook });
  }

  return (
    <div className={clsx(classes.root, classes.clickableRoot)} onClick={openPlaybook}>
      <div className={classes.body}>
        <div className={classes.titleRow}>
          <Typography noWrap className={classes.titleText} sx={{ fontWeight: 600 }}>
            {playbook.title || 'Untitled playbook'}
          </Typography>
          <PlaybookChip label="Generated" tone="passed" />
        </div>
        <Typography className={classes.meta}>
          {getPlaybookSummary({
            categoryCount: playbook.categories.length,
            ruleCount: playbook.ruleCount,
          })}
        </Typography>
      </div>
    </div>
  );
}

/**
 * A run that fails after writing rules keeps them: only a playbook with nothing
 * in it is removed along with the failed run. So the card names that partial
 * playbook and opens it, the same as a finished one, and says why the run
 * stopped underneath. This card is the user's handle on what was written.
 */
function FailedPlaybookGenerationCard({
  generationJob,
  isOnChatRoute,
}: {
  generationJob: {
    failedReason?: string | null;
    playbook?: (OpenablePlaybook & { categories: unknown[]; ruleCount: number }) | null;
  };
  isOnChatRoute: boolean;
}) {
  const { classes } = useStyles();
  const { failedReason, playbook } = generationJob;

  function openPlaybook() {
    if (!playbook) return;
    openGeneratedPlaybook({ isOnChatRoute, playbook });
  }

  return (
    <div
      className={clsx(classes.root, Boolean(playbook) && classes.clickableRoot)}
      onClick={openPlaybook}
    >
      <div className={classes.body}>
        <div className={classes.titleRow}>
          <Typography noWrap className={classes.titleText} sx={{ fontWeight: 600 }}>
            {getFailedCardTitle(playbook)}
          </Typography>
          <PlaybookChip label="Failed" tone="failed" />
        </div>
        <Typography className={classes.meta}>
          {failedReason || 'The playbook could not be generated.'}
        </Typography>
        {playbook && (
          <Typography className={classes.meta}>
            {`Kept ${getPlaybookSummary({
              categoryCount: playbook.categories.length,
              ruleCount: playbook.ruleCount,
            })}`}
          </Typography>
        )}
      </div>
    </div>
  );
}

/**
 * A partial playbook leads with its own name, since that is what the card
 * opens. With nothing left behind there is only the run to report on.
 */
function getFailedCardTitle(playbook: OpenablePlaybook | null | undefined) {
  if (!playbook) return 'Playbook generation failed';
  return playbook.title || 'Untitled playbook';
}

function getPlaybookSummary({
  categoryCount,
  ruleCount,
}: {
  categoryCount: number;
  ruleCount: number;
}) {
  const rules = `${ruleCount} ${ruleCount === 1 ? 'rule' : 'rules'}`;
  const categories = `${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'}`;
  return `${rules} in ${categories}.`;
}
